import { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { api } from "../lib/api";

/**
 * TrendingCategoriesStrip — a small horizontal row of clickable category
 * chips ranked by:
 *     score = recent_bookings_30d * 10 + active_services
 * so booking volume eventually dominates while the strip still has sensible
 * content on a fresh deployment where bookings are sparse.
 *
 * Props:
 *   - selected         currently active category (used to highlight a chip)
 *   - onSelect(name)   parent's category-set callback (defaults to "All")
 */
export default function TrendingCategoriesStrip({ selected, onSelect }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/services/trending-categories", { params: { limit: 5 } })
      .then(({ data }) => { if (!cancelled) setRows(data.trending || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (rows.length === 0) return null;

  return (
    <div
      className="flex items-center gap-2 flex-wrap mb-6"
      data-testid="market-trending-strip"
    >
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-secondary/80 shrink-0">
        <Flame size={11} className="text-secondary" strokeWidth={2} />
        Trending
      </span>
      {rows.map((r) => {
        const active = selected === r.category;
        const label = r.bookings30d > 0
          ? `${r.category} · ${r.bookings30d} booking${r.bookings30d === 1 ? "" : "s"}`
          : `${r.category} · ${r.services} service${r.services === 1 ? "" : "s"}`;
        return (
          <button
            key={r.category}
            type="button"
            onClick={() => onSelect(r.category)}
            className={`group inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs uppercase tracking-wider transition-colors ${active
                ? "border-secondary bg-secondary/15 text-secondary"
                : "border-secondary/25 bg-secondary/[0.04] text-foreground/75 hover:border-secondary/50 hover:text-secondary"}`}
            data-testid={`trending-chip-${r.category}`}
          >
            <span className="font-medium">{r.category}</span>
            <span className="text-[10px] text-foreground/45 group-hover:text-secondary/60">
              {r.bookings30d > 0 ? ` · ${r.bookings30d} booked` : ` · ${r.services} live`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
